import {
  Direction,
  Page,
  PageSet,
  SwitchCondition,
} from "src/pageDefinitions/types";
import { CoreReduxState } from "src/utils/redux/store";
import invariant from "tiny-invariant";
import { ReadonlyDeep } from "type-fest";
import { conditionsPass, resolveSwitch } from "../conditions";

const PAGE_SETS: Record<string, PageSet> = {};

function normalizePath(pathname: string) {
  return pathname?.replace(/^\/|\/$/, "");
}

function expandSwitchCases(switchCondition: SwitchCondition) {
  const cases: SwitchCondition["cases"] = {};
  if (switchCondition.cases.default) {
    cases.default = switchCondition.cases.default;
  }

  Object.keys(switchCondition.cases).forEach((caseName) => {
    const values = caseName.split("|");
    const casePage = switchCondition.cases[caseName];

    invariant(
      !casePage.props?.progressBar,
      `Progress bar is not supported in switch case ${casePage.pathname}. Define on top switch page for all cases.`
    );

    values.forEach((value) => {
      cases[value] = {
        ...casePage,
        pathname: normalizePath(casePage.pathname),
        actions: [
          ...(switchCondition.actions || []),
          ...(casePage.actions || []),
        ],
        props: {
          ...switchCondition.props,
          ...casePage.props,
        },
      };
    });
  });

  // eslint-disable-next-line no-param-reassign
  switchCondition.cases = cases;
}

export function getPageSet(pageSetId: string): PageSet | undefined {
  return PAGE_SETS[pageSetId];
}

export function getPageSetFromPage(pathname: string, state: CoreReduxState) {
  return Object.values(PAGE_SETS).find((pageSet) => {
    const firstRecord = pageSet.pages.find(
      (page) => (page as SwitchCondition).switch || (page as Page).pathname
    );

    if (!firstRecord) {
      return false;
    }

    const firstPage =
      "switch" in firstRecord
        ? resolveSwitch(firstRecord, state)
        : (firstRecord as Page);

    return (
      firstPage?.pathname === pathname &&
      conditionsPass(firstPage.conditions, state, Direction.FORWARD)
    );
  });
}

export function getPageSetPage(pageSet: PageSet, pathname: string) {
  const needle = normalizePath(pathname);
  let ret: Page;
  pageSet?.pages?.find((page) => {
    if ("switch" in page) {
      return Object.values(page.cases).find((casePage) => {
        if (casePage.pathname === needle) {
          ret = casePage;
          return true;
        }
        return false;
      });
    }
    if (page.pathname === needle) {
      ret = page;
      return true;
    }
    return false;
  });
  return ret;
}

export function getPageSetPageIndex(pageSet: PageSet, pathname: string) {
  return getPageIndex(pageSet.pages, pathname);
}
export function getPageIndex(
  pages: (SwitchCondition | Page)[],
  pathname: string
) {
  const needle = normalizePath(pathname);
  return pages?.findIndex((page) => {
    if ("switch" in page) {
      return Object.values(page.cases).find(
        (casePage) => casePage.pathname === needle
      );
    }
    return page.pathname === needle;
  });
}

export function registerPageSet(pageSet: PageSet) {
  invariant(
    !PAGE_SETS[pageSet.id] || PAGE_SETS[pageSet.id] === pageSet,
    `Different PageSet already registered with id ${pageSet.id}`
  );

  // Normalize all saved paths to avoid annoyances
  pageSet.pages.forEach((page) => {
    if ("switch" in page) {
      expandSwitchCases(page);
    } else {
      // eslint-disable-next-line no-param-reassign
      page.pathname = normalizePath(page.pathname);
    }
  });
  PAGE_SETS[pageSet.id] = pageSet;
}

function registerAll(r: any) {
  r.keys().forEach((key: string) => {
    registerPageSet(r(key) as PageSet);
  });
}
registerAll(require.context("./", true, /\.json$/));

export function getPageSetForContext(context: string, state: CoreReduxState) {
  const matchingSets = Object.values(PAGE_SETS)
    .map((pageSet) => {
      if (pageSet.context_type !== context) {
        return null;
      }
      const score = conditionsPass(
        pageSet.conditions,
        state,
        Direction.FORWARD
      );
      if (!score) {
        return null;
      }
      return { pageSet, score };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score);

  return matchingSets[0]?.pageSet;
}

export default PAGE_SETS as ReadonlyDeep<typeof PAGE_SETS>;
